<script setup>
import { ref, computed, watch } from 'vue';
import { Modal, GaugeChart, RadioButton } from '@/components';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  editOwner: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'success']);

const percentage = ref(props.editOwner?.ownership_percentage || 0);
const jobTitle = ref(props.editOwner?.job_title || '');
const attestation = ref(false);

const isButtonDisabled = computed(() => {
  return !attestation.value || !percentage.value || !jobTitle.value;
});

const handleSubmit = () => {
  emit('success', {
    percentage: percentage.value,
    jobTitle: jobTitle.value,
    attestation: attestation.value,
  });
  emit('close');
};

const validatePercentage = () => {
  if (percentage.value < 0) percentage.value = 0;
  if (percentage.value > 100) percentage.value = 100;
};

const onAttestation = () => {
  attestation.value = !attestation.value;
};

watch(
  () => props.visible,
  (visible) => {
    if (visible && props.editOwner) {
      percentage.value = props.editOwner.ownership_percentage || 0;
      jobTitle.value = props.editOwner.job_title || '';
      attestation.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="emit('close')"
  >
    <div class="flex justify-center">
      <div
        class="relative flex flex-col gap-4 rounded bg-white p-4"
        style="width: 400px"
      >
        <section class="flex items-center justify-between">
          <h3 class="text-left text-base text-dark-gray">
            Edit your ownership details
          </h3>
          <svg
            v-if="!enforceValidation"
            class="cursor-pointer"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            @click="emit('close')"
          >
            <path
              d="M18.364 7.05044L16.9498 5.63623L12 10.586L7.05026 5.63623L5.63605 7.05044L10.5858 12.0002L5.63605 16.9499L7.05026 18.3642L12 13.4144L16.9498 18.3642L18.364 16.9499L13.4142 12.0002L18.364 7.05044Z"
              fill="#6C7C9D"
            />
          </svg>
        </section>

        <section
          class="-mb-4 -mt-1 flex items-center justify-center"
          style="margin-left: -10px"
        >
          <GaugeChart
            :width="70"
            :value="percentage"
          />
          <div
            class="w-full text-left"
            style="margin-bottom: 11px"
          >
            <label class="text-xs font-semibold text-neutral-subtle">
              Ownership Percentage
            </label>
            <input
              v-model.number="percentage"
              class="h-8 w-full rounded border border-placeholder-gray bg-off-white p-3 text-xs focus:bg-white focus:outline-none focus:ring-0"
              type="number"
              min="0"
              max="100"
              step="1"
              @input="validatePercentage"
            />
          </div>
        </section>
        <div class="w-full text-left">
          <label class="text-xs font-semibold text-neutral-subtle">
            Job title
          </label>
          <input
            v-model="jobTitle"
            class="h-8 w-full rounded border border-placeholder-gray bg-off-white p-3 text-xs focus:bg-white focus:outline-none focus:ring-0"
          />
        </div>
        <section>
          <radio-button
            :item="'I certify the information about my identity and ownership is correct.'"
            :active="attestation"
            :handle-select="onAttestation"
            custom-classes="text-xs"
          />
        </section>

        <section class="flex gap-3">
          <button
            class="h-12 w-full cursor-pointer rounded-md border border-yellow-500 bg-yellow-500 text-1md text-white hover:bg-light-orange focus:bg-dark-orange disabled:cursor-not-allowed disabled:border-light-gray disabled:bg-light-gray"
            :disabled="isButtonDisabled"
            @click="handleSubmit"
          >
            <svg
              v-if="loading"
              :class="`loading-${variant}`"
              class="loading relative h-5 w-full"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
                stroke="currentColor"
                stroke-width="3"
              />
              <path
                d="M16 1V7L20 4L16 1Z"
                fill="currentColor"
              />
              <path
                d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
                stroke="currentColor"
                stroke-width="3"
              />
            </svg>
            <p v-else>Save changes</p>
          </button>
        </section>
      </div>
    </div>
  </modal>
</template>
<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
